import { type MessageDescriptor } from 'react-intl-next';

import { getConversationStarters } from '@atlassian/conversation-assistant-agent';
import { type usePublish } from '@atlassian/conversation-assistant-pubsub';
import { type Agent } from '@atlassian/conversation-assistant-service';
import { getUserPreferencesFromLS } from '@atlassian/conversation-assistant-store';
// Testing data until we start to load it from client.
export { ChatAgent, CommsCrafterAgent, JobWriterAgent, A11yMateAgent } from './mock-agents';

export type EditorAgent = Pick<Agent, 'id' | 'name' | 'description' | 'creator' | 'visibility'> & {
	// These are from Agent, just renamed to camel case.
	namedId: Agent['named_id'];
	systemPromptTemplate: Agent['system_prompt_template'];
	creatorType: Agent['creator_type'];
	isDefault: Agent['is_default'];

	// Additional fields we can populate on client side
	// color: string;
	// iconInitials: string;
	conversationStarters: (string | MessageDescriptor)[];

	// Populated when creating agent config items in UserInputCommandPaletteWithLogic.
} & (
		| { channelId: string; publish: ReturnType<typeof usePublish> }
		| { channelId?: never; publish?: never }
	);

/**
 * Pulls out the necessary fields from the raw agent data received to create an EditorAgent.
 * We can use this to populate additional fields like color, iconInitials etc.
 *
 * @param agent
 */
export const getEditorAgent = (agent: Agent): EditorAgent => {
	const {
		id,
		name,
		description,
		named_id,
		creator_type,
		system_prompt_template,
		creator,
		visibility,
		is_default,
	} = agent;

	return {
		id,
		name,
		description,
		namedId: named_id,
		creatorType: creator_type,
		systemPromptTemplate: system_prompt_template,
		creator: creator ?? undefined,
		visibility,
		isDefault: is_default,
		conversationStarters: getConversationStarters(agent).combinedConversationStarters,
	};
};

/**
 * This is a function to filter favourited agents from the full list.
 *
 * @param userId string Preferences are keyed by userId.
 * @returns boolean True if the agent is favourited.
 */
export const createFavouriteAgentsFilter = (userId: string) => {
	const userPreferences = getUserPreferencesFromLS(userId);
	const favouriteAgents = userPreferences?.favouriteAgents ?? [];

	return (agent: Agent): boolean => favouriteAgents.includes(agent.id);
};
